// ui/services/trueTodoAggregator.ts
// ✅ 覆蓋全文
//
// Purpose
// - Collect heterogeneous "todo-like" docs (approval flows/requests + raw todos)
// - Normalize via trueTodoAdapter -> produce canonical TrueTodoItem[] for dashboard KPI.
//
// Offline/Demo support (重要)
// - 若未串接 Firebase / client offline：改用 localStorage 掃描，讓儀錶板不再全部 0。
//   （因為各功能表 DEMO 往往存在 localStorage，而非 Firestore）
//
// Notes
// - This repo仍使用 compat API：db.collection(...).get()/onSnapshot()
// - 本檔只做「讀取與彙整」；計算摘要請看 ui/services/todosSummary.ts

import { db } from "../../firebaseClient";
import {
  adaptApprovalFlowDocs,
  adaptApprovalRequestDocs,
  adaptRawTodoDocs,
  dedupeTrueTodos,
  type TrueTodoExcluded,
  type TrueTodoItem,
} from "./trueTodoAdapter";

// --- Types ---
export type TrueTodosResult = {
  ok: boolean;
  trueTodos: TrueTodoItem[];
  excluded: TrueTodoExcluded[];
  rawCount?: number;
  flowCount?: number;
  requestCount?: number;
  debug?: any;
};

// --- LocalStorage fallback helpers ---
function safeJsonParse(s: string): any | null {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function getLS(): Storage | null {
  try {
    // eslint-disable-next-line no-undef
    if (typeof window === "undefined") return null;
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

function isObj(v: any): v is Record<string, any> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function looksLikeFlow(doc: any): boolean {
  if (!doc) return false;
  const stages = (doc as any).stages ?? (doc as any).flowStages;
  return Array.isArray(stages) && stages.length > 0;
}

function looksLikeRequest(doc: any): boolean {
  if (!doc) return false;
  if (looksLikeFlow(doc)) return false;
  const k = Object.keys(doc);
  return (
    k.includes("approvalFlowId") ||
    k.includes("flowId") ||
    k.includes("requestId") ||
    k.includes("currentStageKey") ||
    k.includes("stageKey") ||
    k.includes("approvalStatus")
  );
}

function looksLikeRawTodo(doc: any): boolean {
  if (!doc) return false;
  if (looksLikeFlow(doc)) return false;
  const k = Object.keys(doc);
  return k.includes("todoModule") || k.includes("module") || k.includes("category") || k.includes("type");
}

function normalizeDocId(doc: any, fallback: string): string {
  return String(doc?.id ?? doc?._id ?? doc?.docId ?? doc?.refId ?? doc?.requestId ?? doc?.flowId ?? fallback);
}

function flattenAnyToDocs(value: any, keyHint: string, limit: number): Array<{ id: string; data: any }> {
  const out: Array<{ id: string; data: any }> = [];

  const pushDoc = (d: any, i: number) => {
    if (!d) return;
    const id = normalizeDocId(d, `${keyHint}:${i}`);
    out.push({ id, data: d });
  };

  if (Array.isArray(value)) {
    value.forEach((d, i) => pushDoc(d, i));
    return out.slice(0, limit);
  }

  if (isObj(value)) {
    // common pattern: { items:[...]} / { docs:[...]} / { data:[...] }
    const candidates = ["items", "docs", "data", "list", "rows", "records", "flows", "requests", "todos"];
    for (const c of candidates) {
      if (Array.isArray((value as any)[c])) {
        (value as any)[c].forEach((d: any, i: number) => pushDoc(d, i));
        if (out.length) return out.slice(0, limit);
      }
    }

    // last resort: scan object values (shallow)
    for (const [k, v] of Object.entries(value)) {
      if (Array.isArray(v)) {
        v.forEach((d: any, i: number) => pushDoc(d, i));
      }
      if (out.length >= limit) break;
    }
  }

  return out.slice(0, limit);
}

function loadFromLocalStorage(limitEach = 400): {
  rawTodos: Array<{ id: string; data: any }>;
  flows: Array<{ id: string; data: any }>;
  requests: Array<{ id: string; data: any }>;
  debug: any;
} {
  const ls = getLS();
  const debug: any = { keysScanned: 0, matchedKeys: [] as string[] };

  if (!ls) return { rawTodos: [], flows: [], requests: [], debug: { ...debug, noLocalStorage: true } };

  const rawTodos: Array<{ id: string; data: any }> = [];
  const flows: Array<{ id: string; data: any }> = [];
  const requests: Array<{ id: string; data: any }> = [];

  const keyCount = ls.length;
  for (let i = 0; i < keyCount; i++) {
    const k = ls.key(i);
    if (!k) continue;

    debug.keysScanned++;

    // heuristics: only keys likely related
    const kk = k.toLowerCase();
    const maybeRelevant =
      kk.includes("todo") ||
      kk.includes("approval") ||
      kk.includes("flow") ||
      kk.includes("request") ||
      kk.includes("contract") ||
      kk.includes("seal") ||
      kk.includes("case") ||
      kk.includes("gov") ||
      kk.includes("compliance") ||
      kk.includes("intel") ||
      kk.includes("project");

    if (!maybeRelevant) continue;

    const raw = ls.getItem(k);
    if (!raw) continue;

    const parsed = safeJsonParse(raw);
    if (!parsed) continue;

    const docs = flattenAnyToDocs(parsed, `ls:${k}`, limitEach);
    if (!docs.length) continue;

    // classify each doc
    let touched = 0;
    for (const d of docs) {
      const doc = d.data;
      if (looksLikeFlow(doc)) {
        flows.push(d);
        touched++;
        continue;
      }
      if (looksLikeRequest(doc)) {
        requests.push(d);
        touched++;
        continue;
      }
      if (looksLikeRawTodo(doc)) {
        rawTodos.push(d);
        touched++;
        continue;
      }
    }

    if (touched > 0) debug.matchedKeys.push(k);

    // soft cap to avoid huge memory
    if (rawTodos.length > limitEach && flows.length > limitEach && requests.length > limitEach) break;
  }

  return {
    rawTodos: rawTodos.slice(0, limitEach),
    flows: flows.slice(0, limitEach),
    requests: requests.slice(0, limitEach),
    debug: {
      ...debug,
      rawTodos: rawTodos.length,
      flows: flows.length,
      requests: requests.length,
    },
  };
}

// --- Firebase fetch helper (compat) ---
async function fetchDocsCompat(collectionName: string, perCollectionLimit: number): Promise<Array<{ id: string; data: any }>> {
  // Prefer a stable sort when possible, but fallback if field missing
  try {
    const snap = await (db as any)
      .collection(collectionName)
      .orderBy("updatedAt", "desc")
      .limit(perCollectionLimit)
      .get();
    return snap.docs.map((d: any) => ({ id: d.id, data: d.data() }));
  } catch (e1: any) {
    // if offline / permission / missing index, try without orderBy
    try {
      const snap = await (db as any).collection(collectionName).limit(perCollectionLimit).get();
      return snap.docs.map((d: any) => ({ id: d.id, data: d.data() }));
    } catch (e2: any) {
      // localStorage fallback: return empty here, upper layer decides
      throw e2 ?? e1;
    }
  }
}

function buildTrueTodosFromDocs(
  rawTodoDocs: Array<{ id: string; data: any }>,
  approvalFlowDocs: Array<{ id: string; data: any }>,
  approvalRequestDocs: Array<{ id: string; data: any }>,
  sourceTag: string,
  debugExtra?: any
): TrueTodosResult {
  const excluded: TrueTodoExcluded[] = [];
  const pushExcluded = (ex: TrueTodoExcluded) => excluded.push(ex);

  const rawTodos = adaptRawTodoDocs(rawTodoDocs.map((d) => ({ ...d.data, id: d.id })), pushExcluded);

  const flowById: Record<string, any> = {};
  for (const d of approvalFlowDocs) flowById[d.id] = { ...d.data, id: d.id };

  const requestById: Record<string, any> = {};
  for (const d of approvalRequestDocs) requestById[d.id] = { ...d.data, id: d.id };

  const approvalRequests = adaptApprovalRequestDocs(
    approvalRequestDocs.map((d) => ({ ...d.data, id: d.id })),
    pushExcluded,
    flowById
  );

  const approvalFlows = adaptApprovalFlowDocs(
    approvalFlowDocs.map((d) => ({ ...d.data, id: d.id })),
    pushExcluded,
    requestById
  );

  const trueTodos = dedupeTrueTodos([...approvalFlows, ...approvalRequests]);

  return {
    ok: true,
    trueTodos,
    excluded,
    rawCount: rawTodoDocs.length,
    flowCount: approvalFlowDocs.length,
    requestCount: approvalRequestDocs.length,
    debug: {
      source: sourceTag,
      ...debugExtra,
    },
  };
}

// --- Public API: subscribe ---
export function subscribeTrueTodos(
  onData: (res: TrueTodosResult) => void,
  perCollectionLimit = 200
): () => void {
  // In offline mode, Firestore will throw immediately.
  try {
    const colNames = ["todos", "approvalFlows", "approval_requests"];

    const state: Record<string, Array<{ id: string; data: any }>> = {
      todos: [],
      approvalFlows: [],
      approval_requests: [],
    };

    const unsubscribers: Array<() => void> = [];

    for (const col of colNames) {
      const unsub = (db as any)
        .collection(col)
        .limit(perCollectionLimit)
        .onSnapshot(
          (snap: any) => {
            state[col] = snap.docs.map((d: any) => ({ id: d.id, data: d.data() }));
            const res = buildTrueTodosFromDocs(state.todos, state.approvalFlows, state.approval_requests, "firestore-realtime", { collections: { rawTodos: "todos", flows: "approvalFlows", requests: "approval_requests" } });
            onData(res);
          },
          (err: any) => {
            // If realtime fails, fallback to local storage once.
            const local = loadFromLocalStorage(perCollectionLimit);
            const res = buildTrueTodosFromDocs(local.rawTodos, local.flows, local.requests, "localStorage-fallback", local.debug);
            onData(res);
          }
        );
      unsubscribers.push(unsub);
    }

    return () => unsubscribers.forEach((u) => u());
  } catch (err) {
    const local = loadFromLocalStorage(perCollectionLimit);
    const res = buildTrueTodosFromDocs(local.rawTodos, local.flows, local.requests, "localStorage-fallback", local.debug);
    onData(res);
    return () => {};
  }
}

// --- Public API: one-shot ---
export async function getTrueTodosOnce(
  arg: number | { limit?: number } = 400
): Promise<TrueTodosResult> {
  const perCollectionLimit =
    typeof arg === "number" ? arg : Number.isFinite(Number(arg?.limit)) ? Number(arg?.limit) : 400;

  const fetchFirstNonEmpty = async (candidates: string[]) => {
    let firstDocs: Array<{ id: string; data: any }> | null = null;
    let firstName: string | null = null;
    let lastErr: any = null;

    for (const name of candidates) {
      try {
        const docs = await fetchDocsCompat(name, perCollectionLimit);
        if (firstDocs === null) {
          firstDocs = docs;
          firstName = name;
        }
        if ((docs?.length ?? 0) > 0) return { docs, name };
      } catch (e: any) {
        lastErr = e;
      }
    }

    if (firstDocs !== null && firstName) return { docs: firstDocs, name: firstName };
    if (lastErr) throw lastErr;
    return { docs: [], name: candidates[0] };
  };

  // 1) Try Firestore (prefer the actual collections used by this repo)
  try {
    const rawPick = await fetchFirstNonEmpty(["todos", "raw_todos"]);
    const flowPick = await fetchFirstNonEmpty(["approvalFlows", "approval_flows"]);
    const reqPick = await fetchFirstNonEmpty(["approval_requests", "approvalRequests"]);

    const res = buildTrueTodosFromDocs(
      rawPick.docs,
      flowPick.docs,
      reqPick.docs,
      "firestore-once",
      {
        collections: {
          rawTodos: rawPick.name,
          flows: flowPick.name,
          requests: reqPick.name,
        },
      }
    );

    // If Firestore yields zero but localStorage has demo, use local.
    if ((res.trueTodos?.length ?? 0) === 0) {
      const local = loadFromLocalStorage(perCollectionLimit);
      if (local.rawTodos.length || local.flows.length || local.requests.length) {
        return buildTrueTodosFromDocs(local.rawTodos, local.flows, local.requests, "localStorage-fallback", local.debug);
      }
    }

    return res;
  } catch (err: any) {
    // 2) LocalStorage fallback
    const local = loadFromLocalStorage(perCollectionLimit);
    return buildTrueTodosFromDocs(local.rawTodos, local.flows, local.requests, "localStorage-fallback", {
      ...local.debug,
      firestoreError: String(err?.message ?? err),
    });
  }
}
